import React, { FunctionComponent, useState, useEffect } from "react";
import { Header2 } from "@iventis/styles/src/components/texts";
import { useTheme } from "@mui/material";
import { Theme } from "@emotion/react";
import { StyledColumnFlex } from "@iventis/styles/src/components/forms";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations/content/typed-content";
import { IventisFooter, LoadingComponent, SiteLogoComponent, StyledSubmitButton } from "@iventis/components";
import { LoginPanelContainer } from "@iventis/components/src/login-containers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { permissionsApi } from "@iventis/api/src/api";
import { SUPPORT_EMAIL, queryParams, API_KEY_QUERY_PARAM_KEY } from "@iventis/utilities";
import { TypographyColorsEnum, styled } from "@iventis/styles";
import { LoginBackground } from "./login";

enum VerifiedState {
    loading = "Loading",
    success = "Success",
    verified = "Verified",
    error = "Error",
}

const VerifyEmail: FunctionComponent = () => {
    const translate = useIventisTranslate();

    const [componentState, setComponentState] = useState<VerifiedState>(VerifiedState.loading);

    const apiKey = queryParams[API_KEY_QUERY_PARAM_KEY];

    const handleLoginRedirect = () => {
        // Navigate by setting the href directly, causes a refresh and subsiquently causes axios to reinitialise,
        // this means future network requests won't use the api key it was initialised with
        // eslint-disable-next-line iventis/no-href-assignments
        window.location.href = "/login";
    };

    // if no API key set, redirect to login
    if (apiKey == null) {
        handleLoginRedirect();
    }

    const theme = useTheme<Theme>();
    useEffect(() => {
        // using abort controller to prevent second call on dev (strict mode calls use effects twice)
        const controller = new AbortController();
        // eslint-disable-next-line func-names, wrap-iife
        (async function () {
            try {
                const verifyRequest = await permissionsApi.post<{ email: string }>(`email-verification`, null, {
                    signal: controller.signal,
                    suppressToasts: true,
                });
                if (verifyRequest != null) {
                    setComponentState(VerifiedState.success);
                }
            } catch (err) {
                if (err.response.data === "User is already verified") {
                    setComponentState(VerifiedState.verified);
                } else {
                    setComponentState(VerifiedState.error);
                }
            }
        })();
        return () => controller.abort();
    }, []);
    return (
        <LoginBackground>
            {componentState === VerifiedState.loading ? (
                <LoadingComponentParent>
                    <LoadingComponent />
                </LoadingComponentParent>
            ) : (
                <ParentContainer>
                    <LoginPanelContainer id="middle" style={{ textAlign: "center", gap: "5px" }}>
                        <div style={{ marginBottom: "30px" }}>
                            <SiteLogoComponent />
                        </div>

                        {componentState === VerifiedState.error && (
                            <>
                                <StyledColumnFlex>
                                    <FontAwesomeIcon size="3x" icon={["fas", "info-circle"]} />
                                </StyledColumnFlex>

                                <StyledColumnFlex style={{ marginBottom: "30px" }}>
                                    <Header2 style={{ margin: "10px 0 20px 0" }} color={TypographyColorsEnum.CORE}>
                                        {translate(Content.login.error.unknownError)}
                                    </Header2>

                                    <p style={{ maxWidth: "450px", margin: "auto" }}>
                                        {translate(Content.login2.verification.unverifiedErrorMessage)}{" "}
                                        <a href={`mailto:${SUPPORT_EMAIL}?subject=Unable to verify email`} style={{ fontWeight: "bold", color: theme.primaryColors.focus }}>
                                            {SUPPORT_EMAIL}
                                        </a>
                                    </p>
                                </StyledColumnFlex>
                            </>
                        )}

                        {componentState === VerifiedState.verified && (
                            <>
                                <StyledColumnFlex>
                                    <FontAwesomeIcon size="3x" icon={["fad", "envelope-circle-check"]} />
                                </StyledColumnFlex>

                                <StyledColumnFlex style={{ marginBottom: "30px" }}>
                                    <Header2 style={{ margin: "10px 0 20px 0" }} color={TypographyColorsEnum.CORE}>
                                        {translate(Content.login2.verification.headings.alreadyVerified)}
                                    </Header2>

                                    <p style={{ maxWidth: "300px", margin: "0 auto 20px auto" }}>{translate(Content.login2.verification.alreadyVerifiedMessage)}</p>
                                    <StyledSubmitButton onClick={handleLoginRedirect} style={{ margin: "auto" }} type="submit">
                                        {translate(Content.login2.verification.ctas.continueToLogin)}
                                    </StyledSubmitButton>
                                </StyledColumnFlex>
                            </>
                        )}

                        {componentState === VerifiedState.success && (
                            <>
                                <StyledColumnFlex>
                                    <FontAwesomeIcon size="3x" icon={["fad", "envelope-circle-check"]} />
                                </StyledColumnFlex>

                                <StyledColumnFlex style={{ marginBottom: "30px" }}>
                                    <Header2 style={{ margin: "10px 0 20px 0" }} color={TypographyColorsEnum.CORE}>
                                        {translate(Content.login2.verification.headings.emailVerified)}
                                    </Header2>
                                    <p style={{ maxWidth: "250px", margin: "auto" }}>{translate(Content.login2.verification.accountVerifiedMessage)}</p>
                                </StyledColumnFlex>
                                <StyledColumnFlex>
                                    <StyledSubmitButton onClick={handleLoginRedirect} style={{ margin: "auto" }} type="submit">
                                        {translate(Content.login2.verification.ctas.continueToLogin)}
                                    </StyledSubmitButton>
                                </StyledColumnFlex>
                            </>
                        )}
                    </LoginPanelContainer>
                    <IventisFooter />
                </ParentContainer>
            )}
        </LoginBackground>
    );
};

const LoadingComponentParent = styled.div`
    display: flex;
    place-items: center;
    width: 100vw;
    height: 100vh;
    width: 100dvw;
    height: 100dvh;
`;

const ParentContainer = styled.div`
    display: flex;
    flex-direction: column;
    place-items: center;
    min-height: 100vh;
    min-height: 100dvh;
`;

export default VerifyEmail;
